import { FC, ReactNode } from 'react';

import { useFiskaly } from '#hooks/organization/useFiskaly';
import { useSession } from '#hooks/session/useSession';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { TFunction } from 'i18next';

import { Check, Cross, Flex, Regular, SecondaryButton } from '@zazume/zzm-base';
import { assertUnreachable } from '@zazume/zzm-utils';

import { useCurrentOrganization } from '#/contexts/AuthProvider';
import { useFiscalInformationValidation } from '#/hooks/useFiscalInformationValidation';
import { useI18n } from '#/hooks/useI18n';
import { DocumentIDType } from '#/models';
import { BillingDataValidatedFrom } from '#/models/BillingData';

import { ValidatedFiscalInformationDisplay } from './ValidatedFiscalInformationDisplay';

export type ValidationStatus = 'pending' | 'success' | 'failed';

export interface FiscalInformationValidatorProps {
  validatedAt?: Date;
  validatedFrom?: BillingDataValidatedFrom;
  idNumber: string;
  name: string;
  idNumberType: DocumentIDType;
  dirtyFields: {
    idNumber?: boolean;
    idNumberType?: boolean;
    name?: boolean;
  };
  manualValidationToggle?: ReactNode;
  manualValidationAvailable?: boolean;
}

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
`;

const Container = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: ${theme.spacing.s16};

    ${theme.mq.md} {
      grid-template-columns: 1fr auto;
    }
  `
);

const getButtonText = (status: ValidationStatus, t: TFunction) => {
  switch (status) {
    case 'pending':
      return t('fiscalInformationValidator.button');
    case 'success':
      return t('fiscalInformationValidator.success');
    case 'failed':
      return t('fiscalInformationValidator.failed');
    default:
      assertUnreachable(status);
  }
};

const getIcon = (status: ValidationStatus) => {
  switch (status) {
    case 'success':
      return <Check size={16} />;
    case 'failed':
      return <Cross size={16} />;
    case 'pending':
      return null;
    default:
      assertUnreachable(status);
  }
};

const getButtonVariant = (status: ValidationStatus) => {
  switch (status) {
    case 'success':
      return 'confirm';
    case 'failed':
      return 'danger';
    case 'pending':
      return 'default';
    default:
      assertUnreachable(status);
  }
};

interface CanShowValidatorOptions {
  isFiskalyEnabled: boolean;
  isAdmin: boolean;
  isAgent: boolean;
}

const canShowValidator = (options: CanShowValidatorOptions): boolean => {
  if (!options.isFiskalyEnabled) {
    return false;
  }

  if (options.isAdmin) {
    return true;
  }

  if (options.isAgent) {
    return true;
  }

  return false;
};

export const FiscalInformationValidator: FC<FiscalInformationValidatorProps> = ({
  validatedAt,
  validatedFrom,
  idNumber,
  name,
  idNumberType,
  dirtyFields,
  manualValidationToggle,
  manualValidationAvailable
}) => {
  const { t } = useI18n();
  const { isAdmin, isAgent } = useSession();
  const { isEnabled: isFiskalyEnabled } = useFiskaly();
  const organizationId = useCurrentOrganization();
  const { validationStatus, validateFiscalInformation, fiscalInformationChanged } = useFiscalInformationValidation({
    idNumber,
    name,
    idNumberType,
    organizationId,
    dirtyFields
  });

  const canShow = canShowValidator({ isFiskalyEnabled, isAdmin, isAgent });
  if (!canShow) {
    return null;
  }

  if (validatedAt && !fiscalInformationChanged) {
    return <ValidatedFiscalInformationDisplay validatedAt={validatedAt} validatedFrom={validatedFrom} />;
  }

  const buttonVariant = getButtonVariant(validationStatus);

  return (
    <Container>
      <Flex direction="column" gap="s4">
        <Regular variant="S" color="Gray500">
          {t('fiscalInformationValidator.description')}
        </Regular>
        {validationStatus === 'failed' && (
          <Regular variant="S" color="RedDark1">
            {t('fiscalInformationValidator.failedValidation')}
          </Regular>
        )}
        {validationStatus === 'success' && (
          <Regular variant="S" color="GreenDark1">
            {t('fiscalInformationValidator.successValidation')}
          </Regular>
        )}
        {manualValidationAvailable && validationStatus === 'failed' && manualValidationToggle && (
          <div style={{ marginTop: '8px' }}>{manualValidationToggle}</div>
        )}
      </Flex>
      <Flex direction="column" gap="s8">
        <SecondaryButton
          variant={buttonVariant}
          onClick={validateFiscalInformation}
          disabled={validationStatus !== 'pending'}
          type="button"
        >
          {validationStatus !== 'pending' && <IconWrapper>{getIcon(validationStatus)}</IconWrapper>}
          {getButtonText(validationStatus, t)}
        </SecondaryButton>
      </Flex>
    </Container>
  );
};
